/**
 * Where the person's attention is while live mode runs in the browser
 * (docs/plans/os-voice-conversation.md, step 3.8; J34: "not looking" is the
 * tab hidden or the window unfocused).
 *
 * - `looking` is the tab visible, the window focused and OS not minimised.
 * - `inView` is the cards a yes could save that stand whole on screen now
 *   (the card registry), read again on every event inside OS (throttled or
 *   not) and whenever a card registers or goes.
 * - `lastTouchMs` is the last typing, scrolling or click inside OS's own
 *   surface, throttled, so idle resets only on activity with OS.
 *
 * Each change goes to the call as one `attention` frame. While live mode
 * runs the tab's title says OS's state ("● OS · Listening"), and the title
 * the page had comes back when it ends.
 *
 * Nothing here runs on a timer: a hidden tab throttles timers, so idle and
 * voice activity never depend on one. Every reading is an event's.
 */
import { t, type LiveClientFrame } from '@alluzo/shared';
import { useEffect, useRef } from 'react';

export type LiveAttentionFrame = Extract<LiveClientFrame, { type: 'attention' }>;

/** What the page says of itself: visible, focused, and whether OS is minimised. */
export type PageAttention = { readonly visible: boolean; readonly focused: boolean; readonly minimised: boolean };

/** Whether the person is looking at OS: the tab visible, the window focused, OS not minimised. */
export const lookingOf = ({ visible, focused, minimised }: PageAttention): boolean => visible && focused && !minimised;

/** The `attention` frame for a page, its cards in view and the last touch. */
export const attentionFrameOf = (page: PageAttention, inView: readonly string[], lastTouchMs: number): LiveAttentionFrame => ({
  type: 'attention',
  looking: lookingOf(page),
  app: page.visible ? 'foreground' : 'background',
  inView: [...inView],
  lastTouchMs,
});

/** Whether two frames say the same: a frame that says nothing new is not sent. */
export const sameAttention = (a: LiveAttentionFrame | null, b: LiveAttentionFrame): boolean =>
  a !== null &&
  a.looking === b.looking &&
  a.app === b.app &&
  a.lastTouchMs === b.lastTouchMs &&
  a.inView.length === b.inView.length &&
  a.inView.every((id, index) => b.inView[index] === id);

/** How often activity inside OS counts as a new touch, at most. */
export const ACTIVITY_THROTTLE_MS = 2_000;

/**
 * The throttle for activity: answers whether an event at `now` is a new
 * touch, given when the last one counted (null before any).
 */
export const touchCounts = (lastCounted: number | null, now: number, throttleMs: number = ACTIVITY_THROTTLE_MS): boolean =>
  lastCounted === null || now - lastCounted >= throttleMs;

/**
 * One event inside OS: a new touch when the throttle lets it count, else the
 * last touch as it was. Only the touch is throttled: every event still reads
 * the cards in view again, since a scroll that does not count as a touch can
 * still bring a card whole into view.
 */
export const activityOf = (held: { readonly counted: number | null; readonly lastTouch: number }, now: number): { readonly counted: number | null; readonly lastTouch: number } =>
  touchCounts(held.counted, now) ? { counted: now, lastTouch: now } : held;

/** The tab's title while live mode runs: "● OS · Listening". `stateWord` is the caption word, already in words. */
export const liveTabTitleOf = (stateWord: string): string => t('agent.live.tab_title', { state: stateWord });

/**
 * Where OS's own surface is on the page: the composer, the thread, and any
 * frame that marks itself as OS's (the panel, the side sheet, the OS page).
 * Activity anywhere else is the person working on the page, not with OS.
 */
export const OS_SURFACE_SELECTOR = '[data-os-surface], [data-slot="os-composer"], [data-slot="os-thread-scroll"]';

const insideOs = (target: EventTarget | null): boolean =>
  typeof Element !== 'undefined' && target instanceof Element && target.closest(OS_SURFACE_SELECTOR) !== null;

const pageNow = (minimised: boolean): PageAttention => ({
  visible: typeof document === 'undefined' || document.visibilityState === 'visible',
  focused: typeof document === 'undefined' || document.hasFocus(),
  minimised,
});

export type LiveAttentionInput = {
  /** A version-2 call is live: attention is reported and the title is live mode's. */
  readonly active: boolean;
  /** OS is minimised (its frame hidden behind the pill). */
  readonly minimised: boolean;
  /** The state's caption word, already in words, for the tab's title. */
  readonly stateWord: string | null;
  /** The cards a yes could save that stand whole on screen now, by tool call (the card registry). */
  readonly inView: () => Promise<readonly string[]>;
  /** One `attention` frame, each time what it says changes. */
  readonly onAttention: (frame: LiveAttentionFrame) => void;
  /** Hear each card that comes or goes (the card registry), so a card a turn draws with no event is read in view. Answers the way to stop. */
  readonly onCardsChange?: (listener: () => void) => () => void;
};

export const useLiveAttention = ({ active, minimised, stateWord, inView, onAttention, onCardsChange }: LiveAttentionInput): void => {
  const send = useRef(onAttention);
  const cards = useRef(inView);
  const cardNews = useRef(onCardsChange);
  useEffect(() => {
    send.current = onAttention;
    cards.current = inView;
    cardNews.current = onCardsChange;
  });
  const minimisedNow = useRef(minimised);
  const report = useRef<(() => void) | null>(null);

  // The reports: on every change of the page, of the cards in view, and of activity inside OS.
  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined;
    let sent: LiveAttentionFrame | null = null;
    let activity: { counted: number | null; lastTouch: number } = { counted: null, lastTouch: Date.now() };
    let asked = 0;
    let gone = false;

    const reportNow = (): void => {
      const mine = ++asked;
      void cards.current().then((shown) => {
        if (gone || mine !== asked) return;
        const frame = attentionFrameOf(pageNow(minimisedNow.current), shown, activity.lastTouch);
        if (sameAttention(sent, frame)) return;
        sent = frame;
        send.current(frame);
      });
    };
    report.current = reportNow;

    // Only the touch is throttled; the cards in view are read on every event.
    const touched = (event: Event): void => {
      if (!insideOs(event.target)) return;
      activity = activityOf(activity, Date.now());
      reportNow();
    };

    const options = { capture: true, passive: true } as const;
    document.addEventListener('visibilitychange', reportNow);
    window.addEventListener('focus', reportNow);
    window.addEventListener('blur', reportNow);
    // A scroll inside a card list can bring a card into view: it is a touch, and it moves `inView`.
    for (const kind of ['pointerdown', 'keydown', 'wheel', 'scroll'] as const) document.addEventListener(kind, touched, options);
    // A card a turn draws, or one that goes, changes what is in view with no event of the page's.
    const stopCardNews = cardNews.current?.(reportNow);
    reportNow();
    return () => {
      gone = true;
      stopCardNews?.();
      report.current = null;
      document.removeEventListener('visibilitychange', reportNow);
      window.removeEventListener('focus', reportNow);
      window.removeEventListener('blur', reportNow);
      for (const kind of ['pointerdown', 'keydown', 'wheel', 'scroll'] as const) document.removeEventListener(kind, touched, options);
    };
  }, [active]);

  // Minimising, or bringing OS back, is a change of attention too.
  useEffect(() => {
    minimisedNow.current = minimised;
    report.current?.();
  }, [minimised]);

  // The tab's title: live mode's while it runs, the page's own again after.
  const before = useRef<string | null>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (active && stateWord !== null) {
      if (before.current === null) before.current = document.title;
      document.title = liveTabTitleOf(stateWord);
      return;
    }
    if (before.current !== null) {
      document.title = before.current;
      before.current = null;
    }
  }, [active, stateWord]);
  useEffect(
    () => () => {
      if (before.current !== null && typeof document !== 'undefined') document.title = before.current;
      before.current = null;
    },
    [],
  );
};
